import type { UnknownAction } from 'redux';

import { categoriesSelectors } from 'app-components/flux/categories';
import type { ChartOfAccountProps } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { CoaTypeEnum } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { transactionsTypes } from 'app-components/flux/transactions';
import type { TransactionRecord } from 'app-components/flux/transactions/transactionsTypes';
import { createAppSelector } from 'app-components/utils/core/reselect';

import { isSplitCandidate, type OrderCandidate, UNCATEGORIZED_COA } from './merchantOrderCandidate';

/** No existing memo-concatenation convention in the repo; this feature picks ' - '. */
const MEMO_SEPARATOR = ' - ';

const SALES_TAX_CATEGORY_NAME = 'sales tax';

/**
 * The personal Sales Tax category, or `UNCATEGORIZED_COA` when this dataset has none — its id is
 * dataset-scoped, so it cannot be a constant.
 *
 * Memoised on the categories map rather than resolved once and kept: a sync that adds or renames
 * the category invalidates it, where a module-level cache would stay stale until reload.
 */
export const getSalesTaxCoa = createAppSelector(
  [categoriesSelectors.getCategoriesById],
  (categories): ChartOfAccountProps => {
    // Business datasets carry their own Sales Tax, which is a liability the user owes rather than
    // spend on a purchase.
    const category = categories.find(
      (candidate) => candidate.name?.toLowerCase() === SALES_TAX_CATEGORY_NAME && candidate.usageType !== 'BUSINESS',
    );
    return category?.id ? { id: category.id, type: CoaTypeEnum.CATEGORY } : UNCATEGORIZED_COA;
  },
);

/** preProcessUpdate returns wrappers, not bare actions. `extraActions` is typed `any[]` at source. */
export type ExtraAction = { action: UnknownAction; delay?: number };

/** Never rebuilt from scratch — mutating the stored record keeps server-owned fields intact. */
export const patchTransactionForMatch = (existing: TransactionRecord, candidate: OrderCandidate): TransactionRecord => {
  if (isSplitCandidate(candidate)) {
    // The parent coa must be nulled when a split is added, same as the register's own edit path.
    return existing
      .set('coa', null)
      .set('split', transactionsTypes.mkSplit({ items: candidate.transaction.split.items }));
  }

  const { coa, memo } = candidate.transaction;
  const patched = existing.set('coa', coa);
  if (!memo) {
    return patched;
  }
  const existingMemo = existing.memo?.trim();
  return patched.set('memo', existingMemo ? `${memo}${MEMO_SEPARATOR}${existingMemo}` : memo);
};

/**
 * `bindPromiseAction`'s meta contract, built for `put` rather than a bound dispatch: every write
 * path in `resourceSagaUtils` settles `meta.resolve`/`meta.reject`. Tying the result to this
 * dispatch means an unrelated batch finishing cannot be mistaken for ours.
 *
 * The executor runs synchronously, so both callbacks are assigned before this returns.
 */
export const deferredWrite = () => {
  let resolve!: (value: unknown) => void;
  let reject!: (error: unknown) => void;
  const settled = new Promise((promiseResolve, promiseReject) => {
    resolve = promiseResolve;
    reject = promiseReject;
  });
  return { settled, resolve, reject };
};
